import React, { useState } from 'react';
import { BiPlus, BiTrash } from 'react-icons/bi';
import { Button } from '../../ui';
import { useBuilderEditor } from '../../../features/builder/BuilderEditorContext';
import { QuestionType } from '../../../core/constants/common';
import { constructGridQuestionConfigPayload } from '../../../features/builder/payloadBuilders';
import GridQuestionView from '../../GridQuestionView/GridQuestionView';

// Adds a spanning header/heading over a contiguous range of existing
// columns/rows. Kept as its own small form so "add a plain column" (in the
// parent) stays a single, unambiguous button with nothing else nearby.
const SpanGroupForm = ({ items, onAdd, itemNoun }) => {
  const [fromKey, setFromKey] = useState('');
  const [toKey, setToKey] = useState('');
  const [label, setLabel] = useState('');

  if (items.length < 2) return null;

  const submit = () => {
    if (!fromKey || !toKey) return;
    onAdd(fromKey, toKey, label);
    setFromKey('');
    setToKey('');
    setLabel('');
  };

  return (
    <div className="grid-question-editor__span-form d-flex align-items-center gap-2 flex-wrap">
      <select className="builder-ui-select" value={fromKey} onChange={(e) => setFromKey(e.target.value)}>
        <option value="">From {itemNoun}</option>
        {items.map((item, i) => <option key={item.key} value={item.key}>{item.label || `${itemNoun} ${i + 1}`}</option>)}
      </select>
      <span className="builder-text-secondary">to</span>
      <select className="builder-ui-select" value={toKey} onChange={(e) => setToKey(e.target.value)}>
        <option value="">To {itemNoun}</option>
        {items.map((item, i) => <option key={item.key} value={item.key}>{item.label || `${itemNoun} ${i + 1}`}</option>)}
      </select>
      <input
        className="builder-ui-input flex-grow-1"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Header text (e.g. FY 2026)"
      />
      <Button variant="secondary" size="sm" icon={<BiPlus />} onClick={submit} disabled={!fromKey || !toKey}>Add</Button>
    </div>
  );
};

const GridQuestionEditor = () => {
  const {
    gridQuestionData, fieldeditid, allFormFields,
    addGridQuestionColumn, removeGridQuestionColumn, updateGridQuestionColumnLabel,
    addGridQuestionColumnGroup, removeGridQuestionColumnGroup, updateGridQuestionColumnGroupLabel,
    addGridQuestionRow, removeGridQuestionRow, updateGridQuestionRowLabel,
    addGridQuestionRowGroup, removeGridQuestionRowGroup, updateGridQuestionRowGroupLabel,
    updateGridQuestionCellLink,
  } = useBuilderEditor();

  const {
    columns, columnGroups, rows, rowGroups, cellLinks,
  } = gridQuestionData;

  // A field can be bound to at most one grid cell (server-enforced 1:1), so
  // exclude it from every other cell's picker once it's chosen somewhere in
  // this grid. Other GRID-type fields and the field being edited are never
  // linkable at all.
  const linkedElsewhere = (cellKey) => new Set(
    Object.entries(cellLinks)
      .filter(([key, link]) => key !== cellKey && link?.linkedFieldId)
      .map(([, link]) => link.linkedFieldId),
  );

  // Only simple scalar-answer fields can be linked into a grid cell — the
  // cell's input renders as a single text/number box, which can't represent
  // a dropdown, radio, file upload, etc.
  const linkableFields = allFormFields.filter(
    (f) => f.id !== fieldeditid
      && (f.type === QuestionType.NUMERIC_VALUE || f.type === QuestionType.TEXT_BOX),
  );

  const previewContent = { gridQuestionConfig: constructGridQuestionConfigPayload(gridQuestionData) };

  const labelFor = (list, key) => {
    const index = list.findIndex((item) => item.key === key);
    return list[index]?.label || `#${index + 1}`;
  };

  return (
    <div className="grid-question-editor d-flex flex-column gap-3">
      <div>
        <label className="builder-label">Columns</label>
        <div className="d-flex flex-column gap-2">
          {columns.map((col, colIndex) => (
            <div key={col.key} className="d-flex align-items-center gap-2">
              <input
                className="builder-ui-input flex-grow-1"
                value={col.label || ''}
                onChange={(e) => updateGridQuestionColumnLabel(colIndex, e.target.value)}
                placeholder={`Column ${colIndex + 1}`}
              />
              <Button variant="ghost" icon={<BiTrash />} aria-label="Remove column" onClick={() => removeGridQuestionColumn(colIndex)} />
            </div>
          ))}
        </div>
        <Button variant="secondary" size="sm" icon={<BiPlus />} onClick={addGridQuestionColumn}>Add column</Button>

        {columns.length >= 2 && (
          <div className="grid-question-editor__spans">
            <label className="builder-label builder-label--sm">Header spanning multiple columns (optional)</label>
            {columnGroups.map((group, groupIndex) => (
              <div key={group.key} className="d-flex align-items-center gap-2 mb-1">
                <input
                  className="builder-ui-input flex-grow-1"
                  value={group.label || ''}
                  onChange={(e) => updateGridQuestionColumnGroupLabel(groupIndex, e.target.value)}
                />
                <span className="builder-text-secondary text-nowrap small">
                  {labelFor(columns, group.colKeys[0])} – {labelFor(columns, group.colKeys[group.colKeys.length - 1])}
                </span>
                <Button variant="ghost" icon={<BiTrash />} aria-label="Remove spanning header" onClick={() => removeGridQuestionColumnGroup(groupIndex)} />
              </div>
            ))}
            <SpanGroupForm items={columns} onAdd={addGridQuestionColumnGroup} itemNoun="column" />
          </div>
        )}
      </div>

      <div>
        <label className="builder-label">Rows</label>
        <div className="d-flex flex-column gap-2">
          {rows.map((row, rowIndex) => (
            <div key={row.key} className="d-flex align-items-center gap-2">
              <input
                className="builder-ui-input flex-grow-1"
                value={row.label || ''}
                onChange={(e) => updateGridQuestionRowLabel(rowIndex, e.target.value)}
                placeholder={`Row ${rowIndex + 1} (e.g. Male)`}
              />
              <Button variant="ghost" icon={<BiTrash />} aria-label="Remove row" onClick={() => removeGridQuestionRow(rowIndex)} />
            </div>
          ))}
        </div>
        <Button variant="secondary" size="sm" icon={<BiPlus />} onClick={addGridQuestionRow}>Add row</Button>

        {rows.length >= 2 && (
          <div className="grid-question-editor__spans">
            <label className="builder-label builder-label--sm">Full-width heading above a set of rows (optional)</label>
            {rowGroups.map((group, groupIndex) => (
              <div key={group.key} className="d-flex align-items-center gap-2 mb-1">
                <input
                  className="builder-ui-input flex-grow-1"
                  value={group.label || ''}
                  onChange={(e) => updateGridQuestionRowGroupLabel(groupIndex, e.target.value)}
                />
                <span className="builder-text-secondary text-nowrap small">
                  {labelFor(rows, group.rowKeys[0])} – {labelFor(rows, group.rowKeys[group.rowKeys.length - 1])}
                </span>
                <Button variant="ghost" icon={<BiTrash />} aria-label="Remove row heading" onClick={() => removeGridQuestionRowGroup(groupIndex)} />
              </div>
            ))}
            <SpanGroupForm items={rows} onAdd={addGridQuestionRowGroup} itemNoun="row" />
          </div>
        )}
      </div>

      <div>
        <label className="builder-label">Preview</label>
        {columns.length === 0 || rows.length === 0 ? (
          <div className="builder-text-secondary">Add at least one column and one row, then link cells to questions here.</div>
        ) : (
          <GridQuestionView
            content={previewContent}
            editable
            linkableFields={linkableFields}
            linkedElsewhere={linkedElsewhere}
            onCellLinkChange={updateGridQuestionCellLink}
          />
        )}
      </div>
    </div>
  );
};

export default GridQuestionEditor;
